import { useState, useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useParams, Link, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { BlogList, BlogPost, AdminBlogManager } from "./pages/Blog";
import { Trash2,
  Shield, Phone, Mail, MapPin, Users, CheckCircle, MessageCircle, 
  X, Send, ChevronRight, Building2, AlertTriangle, FileText, Check,
  TrendingUp, Clock, Star, Menu, ArrowRight, Sparkles, Lock, LogOut,
  Volume2, VolumeX
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = "https://www.dentalworkerscomp.com";

// SEO helper: sets canonical URL and meta description
const setPageSEO = (path, description) => {
  // Canonical
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = `${SITE_URL}${path}`;
  // Meta description
  if (description) {
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = description;
  }
};

// Company config - loaded from backend but with defaults
const COMPANY = {
  name: "Trio Insurance & Financial Services",
  shortName: "Trio Insurance",
  tagline: "FINANCIAL SERVICES",
  phone: "(415) 928-2886",
  email: "info@trioins.com",
  website: "https://www.trioins.com",
  address: {
    city: "San Francisco",
    state: "CA",
    region: "San Francisco Bay Area"
  },
  social: {
    facebook: "https://www.facebook.com/800Insurance",
    instagram: "https://www.instagram.com/800insurance/"
  }
};

// Cookie Consent Component
const CookieConsent = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShow(true);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setShow(false);
  };

  const declineCookies = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setShow(false);
  };

  if (!show) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900 border-t border-slate-700 p-4 md:p-6"
      data-testid="cookie-consent"
    >
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="text-white text-sm md:text-base">
          <p>
            We use cookies to enhance your experience, analyze site traffic, and personalize content. 
            By clicking "Accept", you consent to our use of cookies. 
            <Link to="/privacy-policy" className="text-red-400 hover:text-red-300 ml-1">Learn more</Link>
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={declineCookies}
            className="px-6 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-800 transition-colors"
            data-testid="cookie-decline-btn"
          >
            Decline
          </button>
          <button
            onClick={acceptCookies}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            data-testid="cookie-accept-btn"
          >
            Accept
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    setMobileOpen(false);
    const isHome = window.location.pathname === '/';
    if (isHome) {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? "bg-white shadow-lg" : "bg-transparent"
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2">
            <img src="/trio-logo.png" alt="Trio Insurance Financial Services" className="h-14 w-auto" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('services')} className={`font-medium transition-colors ${
              scrolled ? "text-slate-700 hover:text-red-600" : "text-white/90 hover:text-white"
            }`}>Services</button>
            <button onClick={() => scrollToSection('coverage')} className={`font-medium transition-colors ${
              scrolled ? "text-slate-700 hover:text-red-600" : "text-white/90 hover:text-white"
            }`}>Coverage</button>
            <button onClick={() => scrollToSection('faq')} className={`font-medium transition-colors ${
              scrolled ? "text-slate-700 hover:text-red-600" : "text-white/90 hover:text-white"
            }`}>FAQ</button>
            <Link to="/cities" className={`font-medium transition-colors ${
              scrolled ? "text-slate-700 hover:text-red-600" : "text-white/90 hover:text-white"
            }`}>Locations</Link>
            <Link to="/blog" className={`font-medium transition-colors ${
              scrolled ? "text-slate-700 hover:text-red-600" : "text-white/90 hover:text-white"
            }`} data-testid="nav-blog-link">Blog</Link>
            <button onClick={() => scrollToSection('quote')} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg font-semibold transition-all hover:shadow-lg">
              Free Consultation
            </button>
          </div>

          <button 
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2"
            data-testid="mobile-menu-btn"
          >
            <Menu className={scrolled ? "text-slate-900" : "text-white"} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t"
          >
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-slate-700">Services</button>
              <button onClick={() => scrollToSection('coverage')} className="block w-full text-left py-2 text-slate-700">Coverage</button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left py-2 text-slate-700">FAQ</button>
              <Link to="/cities" onClick={() => setMobileOpen(false)} className="block py-2 text-slate-700">Locations</Link>
              <Link to="/blog" onClick={() => setMobileOpen(false)} className="block py-2 text-slate-700">Blog</Link>
              <button onClick={() => scrollToSection('quote')} className="block w-full bg-red-600 text-white text-center py-3 rounded-lg font-semibold">
                Free Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Lead Form Component
const LeadForm = ({ city = "California", citySlug = "california", compact = false }) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    practice_name: "",
    employee_count: "",
    payroll_amount: ""
  });
  const [tcpaConsent, setTcpaConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;
    if (!tcpaConsent) {
      setError("Please check the consent box to continue.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        name: `${formData.first_name} ${formData.last_name}`.trim(),
        city: city,
        city_slug: citySlug,
        tcpa_consent: true
      });
      setSuccess(true);
      setFormData({ first_name: "", last_name: "", email: "", phone: "", practice_name: "", employee_count: "", payroll_amount: "" });
      setTcpaConsent(false);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
      >
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-green-800 mb-2">Thank You!</h3>
        <p className="text-green-700">We'll contact you within 24 hours with your custom quote.</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
      {/* Honeypot - hidden from humans, catches bots */}
      <div className="absolute opacity-0 pointer-events-none" aria-hidden="true" tabIndex={-1}>
        <input
          type="text"
          name="website_url"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className={compact ? "space-y-3" : "grid md:grid-cols-2 gap-4"}>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
          <input
            type="text"
            required
            value={formData.first_name}
            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            placeholder="John"
            data-testid="lead-first-name-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
          <input
            type="text"
            required
            value={formData.last_name}
            onChange={(e) => setFormData({...formData, last_name: e.target.value})}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            placeholder="Smith"
            data-testid="lead-last-name-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            placeholder="doctor@dentaloffice.com"
            data-testid="lead-email-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
          <input
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            placeholder="(555) 123-4567"
            data-testid="lead-phone-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Practice Name</label>
          <input
            type="text"
            value={formData.practice_name}
            onChange={(e) => setFormData({...formData, practice_name: e.target.value})}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            placeholder="Smile Dental Care"
            data-testid="lead-practice-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Number of Employees</label>
          <select
            value={formData.employee_count}
            onChange={(e) => setFormData({...formData, employee_count: e.target.value})}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            data-testid="lead-employees-select"
          >
            <option value="">Select...</option>
            <option value="1-5">1-5 employees</option>
            <option value="6-10">6-10 employees</option>
            <option value="11-25">11-25 employees</option>
            <option value="26-50">26-50 employees</option>
            <option value="50+">50+ employees</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Estimated Total Payroll Amount (Past 12 Months)</label>
        <input
          type="text"
          value={formData.payroll_amount}
          onChange={(e) => setFormData({...formData, payroll_amount: e.target.value})}
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
          placeholder="$150,000"
          data-testid="lead-payroll-input"
        />
      </div>

      {error && (
        <p className="text-red-600 text-sm" data-testid="lead-form-error">{error}</p>
      )}

      {/* TCPA Consent Checkbox */}
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="tcpa-consent"
          checked={tcpaConsent}
          onChange={(e) => setTcpaConsent(e.target.checked)}
          className="mt-1 w-4 h-4 text-red-600 border-slate-300 rounded focus:ring-red-500 flex-shrink-0 cursor-pointer"
          data-testid="tcpa-consent-checkbox"
        />
        <label htmlFor="tcpa-consent" className="text-xs text-slate-500 leading-relaxed cursor-pointer">
          By checking this box, I consent to receive calls, texts, and emails from <strong>Trio Insurance</strong> and 
          its licensed agents at the phone number and email provided, including via automated technology. 
          Message & data rates may apply. Frequency may vary. Reply STOP to opt out. 
          This consent is not a condition of purchase.{" "}
          <Link to="/privacy-policy" className="text-red-600 hover:text-red-700 underline">Privacy Policy</Link>
          {" | "}
          <Link to="/terms" className="text-red-600 hover:text-red-700 underline">Terms & Conditions</Link>
        </label>
      </div>

      <button
        type="submit"
        disabled={loading || !tcpaConsent}
        className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-lg font-bold text-lg transition-all hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="lead-submit-btn"
      >
        {loading ? (
          <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            Request Free Consultation <ArrowRight className="w-5 h-5" />
          </>
        )}
      </button>

      <p className="text-xs text-slate-400 text-center">
        Submitting this form does not guarantee coverage or bind any policy.
      </p>
    </form>
  );
};

// AI Chatbot Component
const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm here to help you learn about Workers Comp insurance for dental offices. What questions do you have?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm having trouble connecting. Please try again or fill out our quote form for immediate assistance." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-24 right-6 z-50 w-16 h-16 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform overflow-hidden"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="chatbot-toggle-btn"
      >
        {isOpen ? (
          <div className="w-full h-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center">
            <X className="w-7 h-7 text-white" />
          </div>
        ) : (
          <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&h=200&fit=crop&crop=face" alt="Chat with Sarah" className="w-full h-full object-cover" />
        )}
      </motion.button>

      {/* Pulse indicator */}
      {!isOpen && (
        <span className="fixed bottom-24 right-6 z-40 w-16 h-16 bg-red-500 rounded-full animate-ping opacity-30" />
      )}

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-44 right-6 z-50 w-96 max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200"
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Insurance Assistant</h4>
                  <p className="text-slate-400 text-sm">Powered by AI • Online now</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4 bg-slate-50">
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === "user" 
                      ? "bg-red-600 text-white rounded-br-md" 
                      : "bg-white text-slate-800 shadow-sm border border-slate-100 rounded-bl-md"
                  }`}>
                    {msg.content}
                  </div>
                </motion.div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm border border-slate-100">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 bg-white border-t border-slate-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about workers comp..."
                  className="flex-1 px-4 py-2.5 border border-slate-300 rounded-full focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  data-testid="chatbot-input"
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="w-10 h-10 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center disabled:opacity-50 transition-colors"
                  data-testid="chatbot-send-btn"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// AI Avatar Exit-Intent Component
const ExitIntentAvatar = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [subtitle, setSubtitle] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [sessionId] = useState(() => `avatar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "", insurance_type: "", message: "" });
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [avatarTcpa, setAvatarTcpa] = useState(false);
  const [formError, setFormError] = useState("");
  const [pendingAudio, setPendingAudio] = useState(null);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const audioRef = useRef(null);

  // Exit intent detection - Desktop (mouse leaves) + Mobile (scroll up after scrolling down)
  useEffect(() => {
    let maxScroll = 0;
    const isMobile = window.innerWidth < 768;

    // Desktop ONLY: mouse leaves viewport
    const handleMouseLeave = (e) => {
      if (e.clientY <= 5 && !hasTriggered && !isVisible) {
        setHasTriggered(true);
        setIsVisible(true);
        playGreeting();
      }
    };

    // Mobile ONLY: user scrolls back up significantly (likely leaving)
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      if (currentScroll > maxScroll) maxScroll = currentScroll;
      if (maxScroll > 500 && (maxScroll - currentScroll) > 300 && !hasTriggered && !isVisible) {
        setHasTriggered(true);
        setIsVisible(true);
        playGreeting();
      }
    };

    if (!isMobile) {
      document.addEventListener("mouseleave", handleMouseLeave);
    } else {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }
    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hasTriggered, isVisible]);

  const playAudio = (base64Audio) => {
    if (!base64Audio) return;
    try {
      const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      audio.play().catch(() => {
        setSpeaking(false);
        setPendingAudio(base64Audio);
        setAudioBlocked(true);
      });
    } catch (e) { setSpeaking(false); }
  };

  const playPendingAudio = () => {
    if (pendingAudio) {
      setAudioBlocked(false);
      playAudio(pendingAudio);
      setPendingAudio(null);
    }
  };

  const playGreeting = async () => {
    try {
      const res = await axios.post(`${API}/avatar/greeting`);
      setSubtitle(res.data.greeting);
      setMessages([{ role: "assistant", content: res.data.greeting }]);
      if (res.data.audio_base64) {
        setPendingAudio(res.data.audio_base64);
        setAudioBlocked(true);
      }
    } catch (e) {
      const fallback = "Hi! Before you go — did you find what you were looking for? We help with all types of dental practice insurance.";
      setSubtitle(fallback);
      setMessages([{ role: "assistant", content: fallback }]);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/avatar/chat`, { message: userMsg, session_id: sessionId });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
      setSubtitle(res.data.response);
      if (res.data.audio_base64) playAudio(res.data.audio_base64);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "Let me connect you with an agent — please share your info below." }]);
      setShowForm(true);
    }
    setLoading(false);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!leadForm.name) { setFormError("Please enter your name."); return; }
    if (!avatarTcpa) { setFormError("Please check the consent box to continue."); return; }
    try {
      await axios.post(`${API}/exit-intent-lead`, leadForm);
      setLeadSubmitted(true);
      setSubtitle("Thank you! A licensed agent will reach out to you shortly.");
      setSpeaking(true);
      setTimeout(() => setSpeaking(false), 2000);
    } catch (e) { alert("Something went wrong. Please try again."); }
  };

  const close = () => {
    setIsVisible(false);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setSpeaking(false);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="exit-intent-modal">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} />
        <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9 }} className="relative bg-white rounded-2xl shadow-2xl overflow-hidden max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="exit-intent-content">
          {/* Close button */}
          <button onClick={close} className="absolute top-3 right-3 z-10 w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors" data-testid="exit-intent-close">
            <X size={16} className="text-slate-600" />
          </button>

          {/* Avatar section */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 text-center">
            <div className="relative inline-block">
              <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&h=200&fit=crop&crop=face" alt="Sarah - Insurance Advisor" className={`w-24 h-24 rounded-full object-cover border-4 transition-all duration-300 ${speaking ? 'border-red-400 shadow-lg shadow-red-400/30 scale-105' : 'border-white/30'}`} data-testid="avatar-image" />
              {speaking && (
                <span className="absolute -bottom-1 -right-1 flex h-5 w-5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500" />
                </span>
              )}
            </div>
            <h3 className="text-white font-semibold mt-3 text-lg">Sarah from Trio Insurance</h3>
            <p className="text-slate-400 text-sm">AI Insurance Assistant</p>
            {/* Subtitle / spoken text */}
            {subtitle && (
              <motion.p initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} key={subtitle} className="text-white/90 text-sm mt-3 leading-relaxed max-w-sm mx-auto" data-testid="avatar-subtitle">
                "{subtitle}"
              </motion.p>
            )}
            {audioBlocked && (
              <button onClick={playPendingAudio} className="mt-3 px-4 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-full transition-all inline-flex items-center gap-1.5" data-testid="avatar-play-audio">
                <Volume2 size={14} /> Tap to hear Sarah
              </button>
            )}
          </div>

          <div className="p-5">
            {!showForm && !leadSubmitted && (
              <>
                {/* Chat messages */}
                {messages.length > 1 && (
                  <div className="max-h-40 overflow-y-auto mb-4 space-y-2">
                    {messages.slice(1).map((msg, i) => (
                      <div key={i} className={`text-sm p-2.5 rounded-xl ${msg.role === "user" ? "bg-red-50 text-red-800 ml-8" : "bg-slate-50 text-slate-700 mr-8"}`}>
                        {msg.content}
                      </div>
                    ))}
                    {loading && <div className="text-sm text-slate-400 animate-pulse">Sarah is typing...</div>}
                  </div>
                )}

                {/* Chat input */}
                <form onSubmit={sendMessage} className="flex gap-2 mb-4">
                  <input type="text" value={input} onChange={e => setInput(e.target.value)} placeholder="Type your question..." className="flex-1 px-4 py-2.5 border border-slate-300 rounded-full text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500" data-testid="avatar-chat-input" />
                  <button type="submit" disabled={loading || !input.trim()} className="w-10 h-10 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center disabled:opacity-50" data-testid="avatar-chat-send">
                    <Send size={16} />
                  </button>
                </form>

                {/* Show form button */}
                <button onClick={() => setShowForm(true)} className="w-full py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold rounded-xl hover:from-red-700 hover:to-red-800 transition-all text-sm" data-testid="avatar-show-form-btn">
                  I'd Like Someone to Contact Me
                </button>
                <p className="text-center text-xs text-slate-400 mt-2">We handle Workers Comp, Malpractice, Liability, Disability & more</p>
                <p className="text-center text-xs text-slate-400 mt-1 italic">Sarah is an AI assistant. For licensed advice, a real agent will follow up.</p>
              </>
            )}

            {showForm && !leadSubmitted && (
              <form onSubmit={submitLead} className="space-y-3" data-testid="exit-intent-form">
                <input type="text" placeholder="Your Name *" value={leadForm.name} onChange={e => setLeadForm({...leadForm, name: e.target.value})} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500" required data-testid="exit-form-name" />
                <div className="grid grid-cols-2 gap-3">
                  <input type="email" placeholder="Email" value={leadForm.email} onChange={e => setLeadForm({...leadForm, email: e.target.value})} className="px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500" data-testid="exit-form-email" />
                  <input type="tel" placeholder="Phone" value={leadForm.phone} onChange={e => setLeadForm({...leadForm, phone: e.target.value})} className="px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500" data-testid="exit-form-phone" />
                </div>
                <select value={leadForm.insurance_type} onChange={e => setLeadForm({...leadForm, insurance_type: e.target.value})} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500" data-testid="exit-form-insurance-type">
                  <option value="">What insurance do you need?</option>
                  <option value="Workers Compensation">Workers Compensation</option>
                  <option value="Dental Malpractice">Dental Malpractice</option>
                  <option value="General Liability">General Liability</option>
                  <option value="Disability Insurance">Disability Insurance</option>
                  <option value="Property Insurance">Property Insurance</option>
                  <option value="Cyber Liability">Cyber Liability</option>
                  <option value="Employment Practices">Employment Practices</option>
                  <option value="Dental Insurance">Dental Insurance</option>
                  <option value="Other">Other / Not Sure</option>
                </select>
                <textarea placeholder="Tell us what you need (optional)" value={leadForm.message} onChange={e => setLeadForm({...leadForm, message: e.target.value})} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 h-20 resize-none" data-testid="exit-form-message" />
                {formError && <p className="text-red-600 text-xs" data-testid="exit-form-error">{formError}</p>}
                <div className="flex items-start gap-2">
                  <input type="checkbox" id="avatar-tcpa" checked={avatarTcpa} onChange={e => setAvatarTcpa(e.target.checked)} className="mt-1 w-4 h-4 text-red-600 border-slate-300 rounded focus:ring-red-500 flex-shrink-0 cursor-pointer" data-testid="exit-form-tcpa" />
                  <label htmlFor="avatar-tcpa" className="text-xs text-slate-500 leading-relaxed cursor-pointer">
                    By checking this box, I consent to receive calls, texts, and emails from <strong>Trio Insurance</strong> and its licensed agents at the phone number and email provided, including via automated technology. Message & data rates may apply. This consent is not a condition of purchase.
                  </label>
                </div>
                <button type="submit" disabled={!avatarTcpa} className="w-full py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold rounded-xl hover:from-red-700 hover:to-red-800 transition-all disabled:opacity-50" data-testid="exit-form-submit">
                  Get My Free Consultation
                </button>
                <p className="text-center text-xs text-slate-400 mt-2 italic">Sarah is an AI assistant. For licensed advice, a real agent will follow up.</p>
              </form>
            )}

            {leadSubmitted && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-4" data-testid="exit-form-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check size={32} className="text-green-600" />
                </div>
                <h4 className="text-lg font-bold text-slate-900">Thank You!</h4>
                <p className="text-slate-500 text-sm mt-1">A licensed agent will reach out to you shortly.</p>
                <button onClick={close} className="mt-4 text-red-600 hover:text-red-700 text-sm font-medium">Close</button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Homepage Component
const Home = () => {
  const [cities, setCities] = useState([]);
  const heroVideoRef = useRef(null);
  const heroMobileVideoRef = useRef(null);
  const [heroMuted, setHeroMuted] = useState(true);

  const toggleHeroSound = () => {
    const isMobile = window.innerWidth < 768;
    const videoRef = isMobile ? heroMobileVideoRef : heroVideoRef;
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      if (!videoRef.current.muted) videoRef.current.volume = 0.3;
      setHeroMuted(videoRef.current.muted);
    }
  };

  useEffect(() => {
    document.title = "Dental Workers Comp Insurance California | Trio Insurance";
    setPageSEO("/", "Get affordable Workers Compensation insurance for your dental practice in California. Serving 194+ cities. Free consultations with licensed agents.");

    // Handle hash scroll (e.g. from blog CTA linking to /#quote)
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }

    // Homepage Schema Markup
    const schemas = [
      {
        id: "schema-organization",
        data: {
          "@context": "https://schema.org",
          "@type": "InsuranceAgency",
          "name": "Trio Insurance & Financial Services",
          "url": "https://www.dentalworkerscomp.com",
          "logo": "https://www.dentalworkerscomp.com/favicon.ico",
          "description": "Specialized Workers Compensation insurance for dental offices across California. Licensed agents, multiple carrier options, free consultations.",
          "telephone": "(415) 928-2886",
          "email": "info@trioins.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "San Francisco",
            "addressRegion": "CA",
            "addressCountry": "US"
          },
          "areaServed": {
            "@type": "State",
            "name": "California"
          },
          "serviceType": "Workers Compensation Insurance",
          "priceRange": "$$",
          "knowsAbout": ["Workers Compensation Insurance", "Dental Office Insurance", "Business Insurance", "California Workers Comp"]
        }
      },
      {
        id: "schema-website",
        data: {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Trio Insurance - Dental Workers Comp",
          "url": "https://www.dentalworkerscomp.com",
          "description": "Workers Compensation insurance for dental offices in California",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.dentalworkerscomp.com/cities?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }
      },
      {
        id: "schema-service",
        data: {
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Workers Compensation Insurance for Dental Offices",
          "serviceType": "Workers Compensation Insurance",
          "provider": {
            "@type": "InsuranceAgency",
            "name": "Trio Insurance & Financial Services",
            "telephone": "(415) 928-2886"
          },
          "areaServed": {
            "@type": "State",
            "name": "California"
          },
          "description": "Specialized workers compensation coverage for dental practices including hygienists, assistants, and office staff. Coverage for needlestick injuries, repetitive strain, chemical exposure, and more.",
          "offers": {
            "@type": "Offer",
            "description": "Free no-obligation consultation and quote comparison from multiple carriers",
            "price": "0",
            "priceCurrency": "USD"
          }
        }
      },
      {
        id: "schema-home-faq",
        data: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Why do dental offices need Workers Compensation insurance?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "California law requires ALL employers, including dental practices, to carry Workers Compensation insurance. Dental offices face unique workplace risks including needlestick injuries, repetitive strain, and chemical exposure that make proper coverage essential."
              }
            },
            {
              "@type": "Question",
              "name": "What does Workers Comp cover for dental employees?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Workers Comp covers medical expenses, lost wages, rehabilitation costs, and disability benefits for dental staff injured on the job. This includes dental hygienists, assistants, front office staff, and dentists themselves."
              }
            },
            {
              "@type": "Question",
              "name": "How much does Workers Comp cost for a dental office?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Premiums vary based on factors like number of employees, payroll size, and claims history. Dental offices typically pay between $0.75 to $2.50 per $100 of payroll. We can provide a customized quote for your practice."
              }
            },
            {
              "@type": "Question",
              "name": "What are common workplace injuries in dental offices?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Common injuries include needlestick injuries, back/neck strain from patient positioning, carpal tunnel from repetitive motions, chemical burns from dental materials, and slip-and-fall accidents."
              }
            }
          ]
        }
      }
    ];

    schemas.forEach(({ id, data }) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    });

    axios.get(`${API}/cities`).then(res => {
      setCities(res.data.cities.slice(0, 12));
    }).catch(console.error);

    // HowTo Schema for Google AI Mode
    const howToSchema = { "@context": "https://schema.org", "@type": "HowTo", "name": "How to Get Workers Compensation Insurance for Your Dental Office", "description": "A simple 4-step process to get workers comp coverage for your dental practice in California.", "step": [
      { "@type": "HowToStep", "name": "Request a Free Consultation", "text": "Fill out our consultation form with your dental practice details, number of employees, and location. This takes less than 2 minutes.", "position": 1 },
      { "@type": "HowToStep", "name": "We Compare Carriers", "text": "Our licensed insurance specialists compare quotes from top carriers like The Hartford, EMPLOYERS, and Zenith to find the best rate for your dental practice.", "position": 2 },
      { "@type": "HowToStep", "name": "Review Your Custom Quote", "text": "We present you with tailored coverage options that match your dental practice size, specialty, and budget. No obligation to purchase.", "position": 3 },
      { "@type": "HowToStep", "name": "Get Covered and Stay Compliant", "text": "Choose your policy and get covered immediately. We handle the paperwork so your dental practice stays compliant with California law.", "position": 4 }
    ]};
    const howToScript = document.createElement('script');
    howToScript.id = 'schema-howto';
    howToScript.type = 'application/ld+json';
    howToScript.textContent = JSON.stringify(howToSchema);
    document.head.appendChild(howToScript);

    // Speakable Schema for homepage (Google AI Mode + Voice Search)
    const homeSpeakableSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Dental Workers Comp Insurance California | Trio Insurance",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["h1", "#services", "#faq", "#coverage"]
      },
      "url": "https://www.dentalworkerscomp.com/"
    };
    let homeSpeakable = document.getElementById('schema-home-speakable');
    if (!homeSpeakable) {
      homeSpeakable = document.createElement('script');
      homeSpeakable.id = 'schema-home-speakable';
      homeSpeakable.type = 'application/ld+json';
      document.head.appendChild(homeSpeakable);
    }
    homeSpeakable.textContent = JSON.stringify(homeSpeakableSchema);

    return () => {
      ['schema-organization', 'schema-website', 'schema-service', 'schema-home-faq', 'schema-howto', 'schema-home-speakable'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const stats = [
    { value: "194+", label: "CA Cities Served" },
    { value: "Licensed", label: "Insurance Agents" },
    { value: "Free", label: "Consultations" },
    { value: "Multiple", label: "Carrier Options" }
  ];

  const risks = [
    { icon: AlertTriangle, title: "Needlestick Injuries", desc: "Protection for sharps-related incidents" },
    { icon: Users, title: "Repetitive Strain", desc: "Coverage for ergonomic injuries" },
    { icon: FileText, title: "Chemical Exposure", desc: "Medical costs for hazmat incidents" },
    { icon: Shield, title: "Slip & Fall", desc: "Workplace accident coverage" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[55vh] lg:min-h-screen overflow-hidden">
        {/* Background Image (loads first) */}
        <div className="absolute inset-0">
          <img 
            src="/dental-team.jpg" 
            alt="Professional dental team" 
            className="w-full h-full object-cover hidden md:block"
            style={{ objectPosition: '50% 20%' }}
          />
          <img 
            src="/dental-team-mobile.jpg" 
            alt="Professional dental team" 
            className="w-full h-full object-cover md:hidden"
          />
          {/* Desktop Video */}
          <video 
            ref={heroVideoRef}
            autoPlay 
            muted 
            loop 
            playsInline
            className="absolute inset-0 w-full h-full object-cover hidden md:block"
            style={{ objectPosition: '50% 20%' }}
            poster="/dental-team.jpg"
          >
            <source src="/dental-hero.mp4" type="video/mp4" />
          </video>
          {/* Mobile Video */}
          <video 
            ref={heroMobileVideoRef}
            autoPlay 
            muted 
            loop 
            playsInline
            className="absolute inset-0 w-full h-full object-cover md:hidden"
            poster="/dental-team-mobile.jpg"
          >
            <source src="/dental-hero-mobile.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/55 to-slate-900/35" />
        </div>

        {/* Unmute Button */}
        <button
          onClick={toggleHeroSound}
          className="absolute bottom-32 sm:bottom-32 left-4 sm:left-8 z-30 flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-full transition-all border border-white/25 animate-pulse hover:animate-none"
          data-testid="hero-unmute-btn"
        >
          {heroMuted ? (
            <>
              <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-xs sm:text-sm font-medium">Hear Our Story</span>
            </>
          ) : (
            <>
              <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-xs sm:text-sm font-medium">Playing...</span>
            </>
          )}
        </button>

        <Navigation />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 lg:pt-32 pb-12 lg:pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[40vh] lg:min-h-[70vh]">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block bg-red-600/20 text-yellow-400 px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-red-600/30">
                Specialized In Workers Comp for Dental Practices
              </span>
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6">
                Learn About <span className="text-red-500">Workers Compensation</span> Options For Your Dental Practice
              </h1>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Connect with licensed insurance professionals who specialize in helping dental offices 
                understand their Workers Compensation options. Request a free, no-obligation consultation today.
              </p>
                  <p className="speakable-intro text-xs text-slate-400 mb-4">DentalWorkersComp.com helps California dental practice owners find, compare, and obtain workers compensation insurance through licensed agents serving 194+ cities.</p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <a 
                  href="#quote" 
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all hover:shadow-xl flex items-center justify-center gap-2"
                  data-testid="hero-cta-btn"
                >
                  Get Free Quote <ChevronRight className="w-5 h-5" />
                </a>
                <a 
                  href="tel:+14159282886" 
                  className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 border border-white/20"
                >
                  <Phone className="w-5 h-5" /> Call Us Now
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + i * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                    <div className="text-sm text-slate-400">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Content - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 2.5 }}
              className="bg-white rounded-2xl p-8 shadow-2xl"
              id="quote"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Request Free Information</h2>
                <p className="text-slate-600">No obligation • A licensed agent will contact you</p>
              </div>
              <LeadForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted Carriers Section */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Our Partners</span>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-3">
              Trusted Insurance Carriers
            </h2>
            <p className="text-slate-600 mt-2">We work with top-rated carriers to find you the best coverage and rates</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center justify-center gap-8 md:gap-12 opacity-80 hover:opacity-100 transition-opacity"
            data-testid="carrier-logos"
          >
            <img src="https://customer-assets.emergentagent.com/job_4afbef5f-57d0-4e3f-bf9f-a0ebabbaab91/artifacts/1etlyyhc_carriers.png" alt="Insurance Carriers - Travelers, Employers, The Zenith, The Hartford, Berkshire Hathaway Guard, Great American, Pie Insurance" className="max-w-full md:max-w-3xl h-auto" />
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-4 mb-4">
              Dental-Specific Workers Comp Expertise
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We understand the unique risks dental practices face. Our coverage is tailored to protect your team from industry-specific hazards.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {risks.map((risk, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border border-slate-100"
              >
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                  <risk.icon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{risk.title}</h3>
                <p className="text-slate-600">{risk.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Section */}
      <section id="coverage" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Complete Protection</span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-4 mb-6">
                What Our Coverage Includes
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                California law requires all employers to carry Workers Compensation insurance. 
                Our dental-specific policies provide comprehensive protection for your entire team.
              </p>

              <div className="space-y-4">
                {[
                  "Medical expenses for workplace injuries",
                  "Lost wages during recovery",
                  "Rehabilitation and therapy costs",
                  "Disability benefits (temporary & permanent)",
                  "Death benefits for surviving family",
                  "Legal defense for work-related claims"
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 text-lg">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1704455306251-b4634215d98f?auto=format&fit=crop&w=800&q=80" 
                  alt="Modern dental office interior" 
                  className="w-full h-80 object-cover"
                />
              </div>
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white mt-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Average Savings</h3>
                    <p className="text-slate-300 text-sm">vs. standard policies</p>
                  </div>
                </div>
                <div className="text-4xl font-bold text-red-400 mb-3">Up to 20%</div>
                <p className="text-slate-300 mb-5 text-sm">
                  Dental offices often qualify for lower rates due to controlled work environments. 
                  Let us find you the best rate.
                </p>
                <a 
                  href="#quote" 
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Compare Rates <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-10"
              >
                <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">FAQ</span>
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-4">
                  Common Questions
                </h2>
              </motion.div>

              <div className="space-y-4">
            {[
              {
                q: "Why do dental offices need Workers Compensation insurance?",
                a: "California law requires ALL employers, including dental practices, to carry Workers Compensation insurance. Dental offices face unique workplace risks including needlestick injuries, repetitive strain, and chemical exposure that make proper coverage essential."
              },
              {
                q: "What does Workers Comp cover for dental employees?",
                a: "Workers Comp covers medical expenses, lost wages, rehabilitation costs, and disability benefits for dental staff injured on the job. This includes dental hygienists, assistants, front office staff, and dentists themselves."
              },
              {
                q: "How much does Workers Comp cost for a dental office?",
                a: "Premiums vary based on factors like number of employees, payroll size, and claims history. Dental offices typically pay between $0.75 to $2.50 per $100 of payroll. We can provide a customized quote for your practice."
              },
              {
                q: "What are common workplace injuries in dental offices?",
                a: "Common injuries include needlestick injuries, back/neck strain from patient positioning, carpal tunnel from repetitive motions, chemical burns from dental materials, and slip-and-fall accidents."
              }
            ].map((faq, i) => (
              <motion.details
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 group"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <span className="text-lg font-semibold text-slate-900 pr-4">{faq.q}</span>
                  <ChevronRight className="w-5 h-5 text-slate-400 transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-6 pb-6 text-slate-600">
                  {faq.a}
                </div>
              </motion.details>
            ))}
          </div>
            </div>

            {/* FAQ Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:sticky lg:top-32"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758205308181-d52b41e00cef?auto=format&fit=crop&w=800&q=80" 
                  alt="Dentist examining a patient" 
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="mt-6 bg-white rounded-xl p-6 shadow-lg border border-slate-100">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-6 h-6 text-red-600" />
                  <span className="font-bold text-slate-900">Protect Your Team Today</span>
                </div>
                <p className="text-slate-600 text-sm mb-4">Your dental staff deserves the best protection. Get a free consultation now.</p>
                <a 
                  href="#quote" 
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cities Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Serving All of California</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-4 mb-4">
              Coverage in Your City
            </h2>
            <p className="text-xl text-slate-600">
              We provide Workers Comp insurance for dental offices across {cities.length > 0 ? "180+" : ""} California cities
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            {cities.map((city, i) => (
              <motion.div
                key={city.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <Link
                  to={`/workers-comp-dental/${city.slug}`}
                  className="flex items-center gap-2 p-4 bg-slate-50 hover:bg-red-50 rounded-xl transition-colors group"
                  data-testid={`city-link-${city.slug}`}
                >
                  <MapPin className="w-4 h-4 text-red-500" />
                  <span className="text-slate-700 group-hover:text-red-600 font-medium">{city.city}</span>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/cities"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 rounded-lg font-semibold transition-colors"
              data-testid="view-all-cities-btn"
            >
              View All California Cities <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured In */}
      <section className="py-10 bg-white border-t border-slate-100" data-testid="as-seen-on-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 text-sm font-medium uppercase tracking-wider mb-5">Trio Insurance Featured In</p>
          <img 
            src="https://customer-assets.emergentagent.com/job_47977543-08b3-417c-80e5-46174f4d3763/artifacts/uwjdwnqy_badge.png" 
            alt="Trio Insurance featured in FOX, Google News, Digital Journal, and Spotify" 
            className="max-w-xs sm:max-w-sm md:max-w-lg mx-auto h-auto opacity-75"
            data-testid="featured-in-img"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-red-600 to-red-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Protect Your Dental Team?
            </h2>
            <p className="text-xl text-red-100 mb-8">
              Get a customized Workers Comp quote in minutes. No obligation, no hassle.
            </p>
            <a
              href="#quote"
              className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-red-600 px-8 py-4 rounded-lg font-bold text-lg transition-colors"
            >
              Request Free Consultation <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />

      <ChatBot />
      <ExitIntentAvatar />
    </div>
  );
};

// Quick Answer Box Component (for AI Search Optimization)
const QuickAnswerBox = ({ question, answer, type }) => {
  const icons = {
    legal_requirement: CheckCircle,
    pricing: TrendingUp,
    coverage: Shield
  };
  const Icon = icons[type] || CheckCircle;
  
  return (
    <div className="quick-answer bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 mb-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="font-bold text-slate-900 text-lg mb-2">{question}</h3>
          <p className="faq-answer text-slate-700 leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  );
};

// City Page Component
const CityPage = () => {
  const { slug } = useParams();
  const [cityData, setCityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cityVideoRef = useRef(null);
  const cityMobileVideoRef = useRef(null);
  const [cityMuted, setCityMuted] = useState(true);

  const toggleCitySound = () => {
    const isMobile = window.innerWidth < 768;
    const videoRef = isMobile ? cityMobileVideoRef : cityVideoRef;
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      if (!videoRef.current.muted) videoRef.current.volume = 0.3;
      setCityMuted(videoRef.current.muted);
    }
  };

  useEffect(() => {
    axios.get(`${API}/cities/${slug}`)
      .then(res => {
        setCityData(res.data);
        document.title = res.data.meta.title;
        
        // Set canonical URL and meta description
        setPageSEO(res.data.meta.canonical, res.data.meta.description);
        
        // Add JSON-LD Schema
        let schemaScript = document.getElementById('schema-local-business');
        if (!schemaScript) {
          schemaScript = document.createElement('script');
          schemaScript.id = 'schema-local-business';
          schemaScript.type = 'application/ld+json';
          document.head.appendChild(schemaScript);
        }
        schemaScript.textContent = JSON.stringify(res.data.schema.local_business);
        
        // Add FAQ Schema
        let faqScript = document.getElementById('schema-faq');
        if (!faqScript) {
          faqScript = document.createElement('script');
          faqScript.id = 'schema-faq';
          faqScript.type = 'application/ld+json';
          document.head.appendChild(faqScript);
        }
        faqScript.textContent = JSON.stringify(res.data.schema.faq);
        
        // Add Speakable Schema (for AI/Voice Search)
        let speakableScript = document.getElementById('schema-speakable');
        if (!speakableScript) {
          speakableScript = document.createElement('script');
          speakableScript.id = 'schema-speakable';
          speakableScript.type = 'application/ld+json';
          document.head.appendChild(speakableScript);
        }
        speakableScript.textContent = JSON.stringify(res.data.schema.speakable);

        // BreadcrumbList Schema for Google search breadcrumbs
        const breadcrumbSchema = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.dentalworkerscomp.com/" },
            { "@type": "ListItem", "position": 2, "name": "Locations", "item": "https://www.dentalworkerscomp.com/cities" },
            { "@type": "ListItem", "position": 3, "name": `Workers Comp - ${res.data.city.city}, CA`, "item": `https://www.dentalworkerscomp.com/workers-comp-dental/${slug}` }
          ]
        };
        let breadcrumbScript = document.getElementById('schema-breadcrumb');
        if (!breadcrumbScript) {
          breadcrumbScript = document.createElement('script');
          breadcrumbScript.id = 'schema-breadcrumb';
          breadcrumbScript.type = 'application/ld+json';
          document.head.appendChild(breadcrumbScript);
        }
        breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
      
    // Cleanup schemas on unmount
    return () => {
      const localBiz = document.getElementById('schema-local-business');
      const faq = document.getElementById('schema-faq');
      const speakable = document.getElementById('schema-speakable');
      const breadcrumb = document.getElementById('schema-breadcrumb');
      if (localBiz) localBiz.remove();
      if (faq) faq.remove();
      if (speakable) speakable.remove();
      if (breadcrumb) breadcrumb.remove();
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!cityData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">City not found</h1>
          <Link to="/" className="text-red-600 hover:underline">Return home</Link>
        </div>
      </div>
    );
  }

  const { city, risks, faq, unique_content, nearby_cities, quick_answers } = cityData;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        {/* Background Image + Video */}
        <div className="absolute inset-0">
          <img 
            src="/dental-team.jpg" 
            alt="Professional dental team" 
            className="w-full h-full object-cover hidden md:block"
            style={{ objectPosition: '50% 20%' }}
          />
          <img 
            src="/dental-team-mobile.jpg" 
            alt="Professional dental team" 
            className="w-full h-full object-cover md:hidden"
          />
          {/* Desktop Video */}
          <video 
            ref={cityVideoRef}
            autoPlay 
            muted 
            loop 
            playsInline
            className="absolute inset-0 w-full h-full object-cover hidden md:block"
            style={{ objectPosition: '50% 20%' }}
            poster="/dental-team.jpg"
          >
            <source src="/dental-hero.mp4" type="video/mp4" />
          </video>
          {/* Mobile Video */}
          <video 
            ref={cityMobileVideoRef}
            autoPlay 
            muted 
            loop 
            playsInline
            className="absolute inset-0 w-full h-full object-cover md:hidden"
            poster="/dental-team-mobile.jpg"
          >
            <source src="/dental-hero-mobile.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/55 to-slate-900/35" />
        </div>

        {/* Unmute Button */}
        <button
          onClick={toggleCitySound}
          className="absolute bottom-32 sm:bottom-8 left-4 sm:left-8 z-30 flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-full transition-all border border-white/25 animate-pulse hover:animate-none"
          data-testid="city-unmute-btn"
        >
          {cityMuted ? (
            <>
              <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-xs sm:text-sm font-medium">Hear Our Story</span>
            </>
          ) : (
            <>
              <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-xs sm:text-sm font-medium">Playing...</span>
            </>
          )}
        </button>

        <Navigation />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Link to="/" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 mb-6">
                <ChevronRight className="w-4 h-4 rotate-180" /> Back to Home
              </Link>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
                Workers Comp for <span className="text-red-500">Dental Offices</span> in {city.city}, CA
              </h1>
              <p className="text-xl text-slate-300 mb-6">
                {unique_content?.intro || `Protect your dental practice in ${city.city} with specialized Workers Compensation coverage.`}
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-white">{city.dental_offices.toLocaleString()}+</div>
                  <div className="text-slate-400">Dental Offices</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-white">{city.county}</div>
                  <div className="text-slate-400">County</div>
                </div>
              </div>

              {unique_content?.premium_range && (
                <div className="hidden">
                  {/* Premium range removed for compliance */}
                </div>
              )}

            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 2.5 }}
              className="bg-white rounded-2xl p-8 shadow-2xl"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Get Your {city.city} Quote</h2>
                <p className="text-slate-600">Customized for dental practices in {city.county} County</p>
              </div>
              <LeadForm city={city.city} citySlug={slug} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted Carriers */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 font-semibold text-sm uppercase tracking-wider mb-8">Trusted Insurance Carriers</p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 opacity-80 hover:opacity-100 transition-opacity">
            <img src="https://customer-assets.emergentagent.com/job_4afbef5f-57d0-4e3f-bf9f-a0ebabbaab91/artifacts/1etlyyhc_carriers.png" alt="Insurance Carriers - Travelers, Employers, The Zenith, The Hartford, Berkshire Hathaway Guard, Great American, Pie Insurance" className="max-w-full md:max-w-3xl h-auto" />
          </div>
        </div>
      </section>

      {/* Quick Answers Section - AI Optimized */}
      {quick_answers && quick_answers.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Quick Answers</span>
              <h2 className="text-3xl font-bold text-slate-900 mt-2">
                Common Questions About Dental Workers Comp in {city.city}
              </h2>
            </div>
            {quick_answers.map((qa, i) => (
              <QuickAnswerBox key={i} question={qa.question} answer={qa.answer} type={qa.type} />
            ))}
          </div>
        </section>
      )}

      {/* Coverage & Savings Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Complete Protection</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-4 mb-6">
                Workers Comp Coverage for {city.city} Dental Offices
              </h2>
              <div className="space-y-4 mb-8">
                {[
                  "Medical expenses & treatment costs",
                  "Lost wages during recovery",
                  "Rehabilitation & physical therapy",
                  "Needlestick & sharps injury protection",
                  "Slip, trip & fall coverage",
                  "Repetitive strain injuries"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1704455306251-b4634215d98f?auto=format&fit=crop&w=800&q=80" 
                  alt="Modern dental office interior" 
                  className="w-full h-80 object-cover"
                />
              </div>
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white mt-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Average Savings</h3>
                    <p className="text-slate-300 text-sm">vs. standard policies</p>
                  </div>
                </div>
                <div className="text-4xl font-bold text-red-400 mb-3">Up to 20%</div>
                <p className="text-slate-300 mb-5 text-sm">
                  Dental offices in {city.city} often qualify for lower rates due to controlled work environments. 
                  Let us find you the best rate.
                </p>
                <button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Compare Rates <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* County-Specific Content Section */}
      {unique_content && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{city.county} County Regulations</h3>
                </div>
                <p className="text-slate-600 leading-relaxed">{unique_content.county_regulations}</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Local Industry Insight</h3>
                </div>
                <p className="text-slate-600 leading-relaxed">{unique_content.industry_insight}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Risks Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Common Dental Workplace Risks in {city.city}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {risks.map((risk, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-slate-50 p-6 rounded-xl"
              >
                <AlertTriangle className="w-8 h-8 text-red-500 mb-3" />
                <p className="text-slate-700">{risk}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Cities Section */}
      {nearby_cities && nearby_cities.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
              Also Serving Nearby {city.county} County Cities
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {nearby_cities.map((nearbyCity) => (
                <Link
                  key={nearbyCity.slug}
                  to={`/workers-comp-dental/${nearbyCity.slug}`}
                  className="bg-white px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-shadow flex items-center gap-2 group"
                >
                  <MapPin className="w-4 h-4 text-red-500" />
                  <span className="text-slate-700 group-hover:text-red-600 font-medium">{nearbyCity.city}</span>
                  <span className="text-slate-400 text-sm">({nearbyCity.dental_offices} offices)</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-8">
                Workers Comp FAQ for {city.city} Dental Offices
              </h2>
              <div className="space-y-4">
                {faq.map((item, i) => (
                  <details key={i} className="bg-slate-50 rounded-xl shadow-sm border border-slate-200 group">
                    <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                      <span className="text-lg font-semibold text-slate-900 pr-4">{item.question}</span>
                      <ChevronRight className="w-5 h-5 text-slate-400 transition-transform group-open:rotate-90" />
                    </summary>
                    <div className="px-6 pb-6 text-slate-600">{item.answer}</div>
                  </details>
                ))}
              </div>
            </div>
            <div className="lg:sticky lg:top-32">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758205308181-d52b41e00cef?auto=format&fit=crop&w=800&q=80" 
                  alt="Dentist examining a patient" 
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="mt-6 bg-white rounded-xl p-6 shadow-lg border border-slate-100">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-6 h-6 text-red-600" />
                  <span className="font-bold text-slate-900">Protect Your {city.city} Practice</span>
                </div>
                <p className="text-slate-600 text-sm mb-4">Get a free, no-obligation consultation for your dental office.</p>
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Protect Your {city.city} Dental Practice?
          </h2>
          <p className="text-red-100 mb-8">
            Join {city.dental_offices.toLocaleString()}+ dental offices in {city.city} with proper Workers Comp coverage.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-white text-red-600 px-8 py-4 rounded-lg font-bold hover:bg-slate-100 transition-colors"
          >
            Request Free Consultation
          </button>
        </div>
      </section>

      {/* Featured In */}
      <section className="py-10 bg-white border-t border-slate-100" data-testid="city-featured-in-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 text-sm font-medium uppercase tracking-wider mb-5">Trio Insurance Featured In</p>
          <img 
            src="https://customer-assets.emergentagent.com/job_47977543-08b3-417c-80e5-46174f4d3763/artifacts/uwjdwnqy_badge.png" 
            alt="Trio Insurance featured in FOX, Google News, Digital Journal, and Spotify" 
            className="max-w-xs sm:max-w-sm md:max-w-lg mx-auto h-auto opacity-75"
            data-testid="city-featured-in-img"
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-4">
            <img src="/trio-logo.png" alt="Trio Insurance Financial Services" className="h-12 w-auto" />
          </div>
          <p className="text-slate-400 mb-2">
            Workers Comp Insurance for Dental Offices in {city.city}, California
          </p>
          <p className="text-slate-500 text-sm mb-1">
            CA Insurance License #0C66738
          </p>
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} Trio Insurance Financial Services. All rights reserved.
          </p>
        </div>
      </footer>

      <ChatBot />
      <ExitIntentAvatar />
    </div>
  );
};

// All Cities Page
const CitiesPage = () => {
  const [cities, setCities] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    document.title = "Workers Comp for Dental Offices - All California Cities | Trio Insurance";
    setPageSEO("/cities", "Find Workers Compensation insurance for dental offices across 194+ California cities. Browse by city or county.");
    axios.get(`${API}/cities`).then(res => setCities(res.data.cities));
  }, []);

  const filtered = cities.filter(c => 
    c.city.toLowerCase().includes(search.toLowerCase()) ||
    c.county.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Workers Comp for Dental Offices in California
            </h1>
            <p className="text-xl text-slate-600">
              Serving {cities.length}+ cities across California
            </p>
          </div>

          <div className="max-w-xl mx-auto mb-12">
            <input
              type="text"
              placeholder="Search by city or county..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-6 py-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
              data-testid="city-search-input"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filtered.map((city) => (
              <Link
                key={city.slug}
                to={`/workers-comp-dental/${city.slug}`}
                className="bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 group"
              >
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-red-500" />
                  <span className="font-semibold text-slate-900 group-hover:text-red-600 transition-colors">
                    {city.city}
                  </span>
                </div>
                <div className="text-sm text-slate-500">
                  {city.county} County • {city.dental_offices} offices
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-600">No cities found matching "{search}"</p>
            </div>
          )}
        </div>
      </div>

      <ChatBot />
      <ExitIntentAvatar />
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "Privacy Policy | Trio Insurance & Financial Services";
    setPageSEO("/privacy-policy", "Privacy Policy for Trio Insurance & Financial Services. Learn how we collect, use, and protect your personal information.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      <div className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Privacy Policy</h1>
          
          <div className="prose prose-slate max-w-none">
            <p className="text-lg text-slate-600 mb-6">
              Trio Insurance & Financial Services values your privacy and is committed to protecting your personal information. This Privacy Policy describes how we collect, use, disclose, and protect your personal information when you visit our website or use our services.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Information We Collect</h2>
            <p className="text-slate-600 mb-4">We collect different types of personal information depending on how you interact with our services:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>When you request a quote, purchase insurance, or communicate with us, we may collect your name, contact details (email, phone number, address), insurance policy details, and payment information.</li>
              <li>When you use our website, we automatically gather information about your device, IP address, browser type, and how you interact with the site, including pages viewed, time spent, and referring URLs.</li>
              <li>Any communication you have with us—via phone, email, or online chat—may be recorded and stored for quality assurance, training, or to help fulfill your requests.</li>
              <li>We use cookies, web beacons, and similar technologies to personalize your experience, improve our services, and track user activity on our website.</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>To process insurance applications, manage policies, and handle claims.</li>
              <li>To respond to inquiries, process requests, and provide necessary customer service.</li>
              <li>To send you relevant offers, promotions, or newsletters based on your preferences (with opt-out options available).</li>
              <li>To monitor how our website is used, improve its functionality, and develop new products and services.</li>
              <li>To protect against fraud, unauthorized access, and other threats, and to comply with legal or regulatory requirements.</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Sharing and Disclosure of Information</h2>
            <p className="text-slate-600 mb-4">We respect your privacy and will not sell, rent, or share your personal information for marketing purposes without your consent. However, we may share your information in the following circumstances:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>We work with trusted third-party vendors to provide services such as data analysis, payment processing, email delivery, and customer support. These providers are obligated to protect your personal information.</li>
              <li>We may disclose your personal information if required by law or in response to legitimate requests from regulatory authorities or courts.</li>
              <li>In the event of a merger, acquisition, or sale of all or part of our assets, your personal information may be transferred to the new business entity.</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Data Security</h2>
            <p className="text-slate-600 mb-6">
              We take your data security seriously and implement various administrative, technical, and physical security measures to protect your personal information. These safeguards are designed to prevent unauthorized access, misuse, or disclosure of your data.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Your Privacy Rights</h2>
            <p className="text-slate-600 mb-4">You have certain rights regarding your personal information, including:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>You can request a copy of the personal data we hold about you.</li>
              <li>You can request that we update or correct any inaccurate personal data.</li>
              <li>Under certain circumstances, you may request that we delete your personal data.</li>
              <li>You may object to the processing of your personal data for certain purposes, including marketing.</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">California Privacy Rights (CCPA)</h2>
            <p className="text-slate-600 mb-6">
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect, the right to delete your information, and the right to opt-out of the sale of your personal information. We do not sell personal information.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Contact Us</h2>
            <p className="text-slate-600 mb-6">
              If you have any questions about this Privacy Policy, please contact us at:<br />
              <strong>Email:</strong> {COMPANY.email}<br />
              <strong>Phone:</strong> {COMPANY.phone}
            </p>
          </div>

          <div className="mt-8">
            <Link to="/" className="text-red-600 hover:text-red-700 font-medium">← Back to Home</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Terms and Conditions Page
const TermsConditions = () => {
  useEffect(() => {
    document.title = "Terms and Conditions | Trio Insurance & Financial Services";
    setPageSEO("/terms", "Terms and Conditions for Trio Insurance & Financial Services website and services.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      <div className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Terms and Conditions</h1>
          
          <div className="prose prose-slate max-w-none">
            <p className="text-lg text-slate-600 mb-6">
              Welcome to Trio Insurance & Financial Services. By accessing or using our website and services, you agree to comply with these Terms and Conditions. Please read these Terms carefully.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">1. Eligibility</h2>
            <p className="text-slate-600 mb-6">
              Our services are intended for individuals who are at least 18 years of age. Using our website or services confirms that you meet this eligibility requirement. If you are using our services on behalf of a company or organization, you represent that you have the authority to bind that entity to these Terms.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">2. Insurance Services</h2>
            <p className="text-slate-600 mb-6">
              We provide insurance-related services, including but not limited to providing insurance quotes, managing policies, and facilitating claims. All services we provide are subject to the availability and approval of the respective insurance provider and applicable U.S. state and federal laws.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">3. Quotations and Policy Information</h2>
            <p className="text-slate-600 mb-6">
              Insurance quotes from our agency are estimates based on the information you provide. These estimates do not constitute binding contracts. The final terms of any insurance policy, including coverage, rates, and exclusions, are determined by the respective insurance provider and are subject to their approval process.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">4. Accuracy of Information</h2>
            <p className="text-slate-600 mb-6">
              You are responsible for providing accurate and complete information when using our services. Failure to do so may result in denial of coverage or cancellation of your policy. We are not responsible for any consequences arising from your failure to provide accurate information.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">5. Prohibited Uses</h2>
            <p className="text-slate-600 mb-4">You agree not to use our website or services for any unlawful purpose or in any way that could damage our services. Additionally, you agree not to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>Impersonate another person or misrepresent your identity.</li>
              <li>Access our systems without authorization.</li>
              <li>Engage in any fraudulent or illegal activities.</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">6. Intellectual Property</h2>
            <p className="text-slate-600 mb-6">
              All content on our website, including but not limited to text, graphics, logos, and software, is owned by or licensed to Trio Insurance & Financial Services and is protected by U.S. intellectual property laws. You may not copy, reproduce, or distribute any content from our website without our express written permission.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">7. Liability Disclaimer</h2>
            <p className="text-slate-600 mb-6">
              Our services are provided "as is" without warranties of any kind, either express or implied. To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, special, or consequential damages resulting from your use of our website or services.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">8. Governing Law</h2>
            <p className="text-slate-600 mb-6">
              Any disputes arising out of or related to these Terms or our services shall be governed by the laws of the state of California, without regard to its conflict of laws principles. You agree to submit to the exclusive jurisdiction of the state and federal courts located in San Francisco, California.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">9. Contact Us</h2>
            <p className="text-slate-600 mb-6">
              If you have any questions about these Terms, please contact us at:<br />
              <strong>Email:</strong> {COMPANY.email}<br />
              <strong>Phone:</strong> {COMPANY.phone}
            </p>
          </div>

          <div className="mt-8">
            <Link to="/" className="text-red-600 hover:text-red-700 font-medium">← Back to Home</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Reusable Footer Component
const Footer = () => (
  <footer className="bg-slate-900 text-white py-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-12">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src="/trio-logo.png" alt="Trio Insurance Financial Services" className="h-12 w-auto" />
          </div>
          <p className="text-slate-400">
            Specialized Workers Compensation insurance for California businesses.
          </p>
          <div className="flex gap-4 mt-4">
            {COMPANY.social.facebook && (
              <a href={COMPANY.social.facebook} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
            )}
            {COMPANY.social.instagram && (
              <a href={COMPANY.social.instagram} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
            )}
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-4">Quick Links</h4>
          <ul className="space-y-2 text-slate-400">
            <li><a href="#services" className="hover:text-white transition-colors">Services</a></li>
            <li><a href="#coverage" className="hover:text-white transition-colors">Coverage</a></li>
            <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
            <li><Link to="/cities" className="hover:text-white transition-colors">Locations</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-4">Contact</h4>
          <ul className="space-y-2 text-slate-400">
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </li>
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4" /> {COMPANY.email}
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="w-4 h-4" /> {COMPANY.address.region}
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-4">Legal</h4>
          <ul className="space-y-2 text-slate-400">
            <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link></li>
            <li><a href="https://www.trioinsureme.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Main Website</a></li>
            <li><a href="https://www.compareworkerscomp.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Workers Comp Quotes</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800 mt-12 pt-8 text-slate-500">
        <p className="text-center">&copy; {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</p>
        <p className="text-sm mt-2 text-center">Licensed Insurance Agency | CA License #0C66738</p>
        
        <div className="mt-6 pt-6 border-t border-slate-800 text-xs text-slate-600 max-w-4xl mx-auto space-y-3">
          <p>
            <strong>IMPORTANT DISCLAIMER:</strong> This website is operated by a licensed insurance agency and is not affiliated with or endorsed by any government agency. Information provided is for general informational purposes only and does not constitute insurance, legal, or financial advice.
          </p>
          <p>
            No coverage will be bound, added, altered, or changed as a result of submitting a request for information. All coverage is subject to the terms, conditions, and exclusions of the actual policy issued. Quotes and information provided are estimates only; actual terms are determined by the insurance carrier based on underwriting criteria.
          </p>
          <p>
            Workers Compensation requirements vary by state, business type, and individual circumstances. California Labor Code Section 3700 generally requires employers to secure workers compensation coverage; however, specific requirements and exemptions depend on individual situations. Consult with a licensed insurance professional and/or legal counsel to determine your specific obligations.
          </p>
          <p>
            By using this website, you acknowledge that you have read and understood this disclaimer. Use of this site is at your own risk.
          </p>
        </div>
      </div>
    </div>
  </footer>
);

// Admin Login Component
const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("adminToken", res.data.token);
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mx-auto mb-4">
            <img src="/trio-logo.png" alt="Trio Insurance" className="h-16 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-slate-400 mt-1">Sign in to access your dashboard</p>
        </div>

        <form onSubmit={handleLogin} className="bg-slate-800 rounded-2xl p-8 shadow-xl border border-slate-700" data-testid="admin-login-form">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-300 mb-2">Username</label>
            <div className="relative">
              <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter username"
                required
                data-testid="admin-username-input"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter password"
                required
                data-testid="admin-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="admin-login-btn"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>Sign In</>
            )}
          </button>
        </form>

        <div className="text-center mt-6">
          <Link to="/" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
            ← Back to website
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("adminToken");
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

// Admin Dashboard
// ==================== INDUSTRY CITY PAGE (KEYWORD EXPANSION) ====================
const IndustryCityPage = () => {
  const { slug } = useParams();
  const location = window.location.pathname;
  const pageType = location.split('/')[1]; // e.g., "dental-malpractice-insurance"
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const industryVideoRef = useRef(null);
  const industryMobileVideoRef = useRef(null);
  const [industryMuted, setIndustryMuted] = useState(true);

  const toggleIndustrySound = () => {
    const isMobile = window.innerWidth < 768;
    const videoRef = isMobile ? industryMobileVideoRef : industryVideoRef;
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      if (!videoRef.current.muted) videoRef.current.volume = 0.3;
      setIndustryMuted(videoRef.current.muted);
    }
  };

  useEffect(() => {
    axios.get(`${API}/industry-city-page-data/${pageType}/${slug}`)
      .then(res => {
        setPageData(res.data);
        document.title = res.data.meta.title;
        setPageSEO(res.data.meta.canonical, res.data.meta.description);
        
        // Service Schema
        let svcScript = document.getElementById('schema-service-industry');
        if (!svcScript) { svcScript = document.createElement('script'); svcScript.id = 'schema-service-industry'; svcScript.type = 'application/ld+json'; document.head.appendChild(svcScript); }
        svcScript.textContent = JSON.stringify(res.data.schema.service);
        
        // FAQ Schema
        let faqScript = document.getElementById('schema-faq-industry');
        if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = 'schema-faq-industry'; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
        faqScript.textContent = JSON.stringify(res.data.schema.faq);
        
        // Speakable Schema
        let spkScript = document.getElementById('schema-speakable-industry');
        if (!spkScript) { spkScript = document.createElement('script'); spkScript.id = 'schema-speakable-industry'; spkScript.type = 'application/ld+json'; document.head.appendChild(spkScript); }
        spkScript.textContent = JSON.stringify(res.data.schema.speakable);

        // BreadcrumbList Schema for industry pages
        const pageLabel = pageType.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        const industryBreadcrumbSchema = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.dentalworkerscomp.com/" },
            { "@type": "ListItem", "position": 2, "name": pageLabel, "item": `https://www.dentalworkerscomp.com/${pageType}` },
            { "@type": "ListItem", "position": 3, "name": `${pageLabel} - ${res.data.city.city}, CA`, "item": `https://www.dentalworkerscomp.com/${pageType}/${slug}` }
          ]
        };
        let bcScript = document.getElementById('schema-breadcrumb-industry');
        if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = 'schema-breadcrumb-industry'; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
        bcScript.textContent = JSON.stringify(industryBreadcrumbSchema);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    
    return () => {
      ['schema-service-industry', 'schema-faq-industry', 'schema-speakable-industry', 'schema-breadcrumb-industry'].forEach(id => {
        const el = document.getElementById(id); if (el) el.remove();
      });
    };
  }, [pageType, slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!pageData) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-center"><h1 className="text-2xl font-bold text-slate-900 mb-4">Page not found</h1><Link to="/" className="text-red-600 hover:underline">Return home</Link></div></div>;

  const { city, faqs, nearby_cities, content_focus } = pageData;
  const cn = city.city;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/dental-team.jpg" alt="Professional dental team" className="w-full h-full object-cover hidden md:block" style={{ objectPosition: '50% 20%' }} />
          <img src="/dental-team-mobile.jpg" alt="Professional dental team" className="w-full h-full object-cover md:hidden" />
          <video ref={industryVideoRef} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover hidden md:block" style={{ objectPosition: '50% 20%' }} poster="/dental-team.jpg"><source src="/dental-hero.mp4" type="video/mp4" /></video>
          <video ref={industryMobileVideoRef} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover md:hidden" poster="/dental-team-mobile.jpg"><source src="/dental-hero-mobile.mp4" type="video/mp4" /></video>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/55 to-slate-900/35" />
        </div>

        <button onClick={toggleIndustrySound} className="absolute bottom-32 sm:bottom-8 left-4 sm:left-8 z-30 flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-full transition-all border border-white/25 animate-pulse hover:animate-none" data-testid="industry-unmute-btn">
          {industryMuted ? <><VolumeX className="w-4 h-4 sm:w-5 sm:h-5" /><span className="text-xs sm:text-sm font-medium">Hear Our Story</span></> : <><Volume2 className="w-4 h-4 sm:w-5 sm:h-5" /><span className="text-xs sm:text-sm font-medium">Playing...</span></>}
        </button>

        <Navigation />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              <Link to="/" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 mb-6"><ChevronRight className="w-4 h-4 rotate-180" /> Back to Home</Link>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">{pageData.h1}</h1>
              <p className="text-xl text-slate-300 mb-6 intro-text">{pageData.intro}</p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white/10 rounded-xl p-4"><div className="text-2xl font-bold text-white">{city.dental_offices.toLocaleString()}+</div><div className="text-slate-400">Dental Offices</div></div>
                <div className="bg-white/10 rounded-xl p-4"><div className="text-2xl font-bold text-white">{city.county}</div><div className="text-slate-400">County</div></div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 2.5 }} className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Get Your {cn} Quote</h2>
                <p className="text-slate-600">Customized {content_focus} for dental practices in {city.county} County</p>
              </div>
              <LeadForm city={cn} citySlug={slug} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted Carriers */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 font-semibold text-sm uppercase tracking-wider mb-8">Trusted Insurance Carriers</p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 opacity-80 hover:opacity-100 transition-opacity">
            <img src="https://customer-assets.emergentagent.com/job_4afbef5f-57d0-4e3f-bf9f-a0ebabbaab91/artifacts/1etlyyhc_carriers.png" alt="Insurance Carriers" className="max-w-full md:max-w-3xl h-auto" />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50 faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl font-bold text-slate-900 mt-2">Common Questions About {content_focus} in {cn}</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900 mb-2">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-red-700 to-red-800 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Get {content_focus} for Your {cn} Dental Practice</h2>
          <p className="text-xl text-red-100 mb-8">Free consultation with licensed insurance specialists. Compare quotes from top carriers.</p>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="bg-white text-red-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-red-50 transition-all shadow-xl">
            Get Your Free Quote <ArrowRight className="w-5 h-5 inline ml-2" />
          </button>
        </div>
      </section>

      {/* Nearby Cities */}
      {nearby_cities && nearby_cities.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Also Serving Nearby Cities in {city.county} County</h2>
            <div className="flex flex-wrap gap-3">
              {nearby_cities.map((nc, i) => (
                <Link key={i} to={`/${pageType}/${nc.slug}`} className="bg-slate-50 hover:bg-red-50 hover:text-red-700 text-slate-700 px-4 py-2 rounded-lg transition-all font-medium">
                  {nc.city}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured In */}
      <section className="py-10 bg-white border-t border-slate-100" data-testid="industry-featured-in-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 text-sm font-medium uppercase tracking-wider mb-5">Trio Insurance Featured In</p>
          <img 
            src="https://customer-assets.emergentagent.com/job_47977543-08b3-417c-80e5-46174f4d3763/artifacts/uwjdwnqy_badge.png" 
            alt="Trio Insurance featured in FOX, Google News, Digital Journal, and Spotify" 
            className="max-w-xs sm:max-w-sm md:max-w-lg mx-auto h-auto opacity-75"
            data-testid="industry-featured-in-img"
          />
        </div>
      </section>

      <Footer />
      <ChatBot />
      <ExitIntentAvatar />
    </div>
  );
};

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("leads");
  const [indexNowStatus, setIndexNowStatus] = useState(null);
  const [indexNowLoading, setIndexNowLoading] = useState(false);
  const [indexNowResult, setIndexNowResult] = useState(null);
  const navigate = useNavigate();

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
  });

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  const loadIndexNowStatus = async () => {
    try { const res = await axios.get(`${API}/admin/indexnow/status`, getAuthHeaders()); setIndexNowStatus(res.data); } catch (e) { console.error('IndexNow status error', e); }
  };

  const submitAllToIndexNow = async () => {
    setIndexNowLoading(true); setIndexNowResult(null);
    try { const res = await axios.post(`${API}/admin/indexnow/submit-all`, {}, getAuthHeaders()); setIndexNowResult(res.data); loadIndexNowStatus(); } catch (e) { setIndexNowResult({ error: 'Submission failed. Try again.' }); }
    setIndexNowLoading(false);
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/leads`, getAuthHeaders()),
      axios.get(`${API}/leads/stats`, getAuthHeaders())
    ]).then(([leadsRes, statsRes]) => {
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    }).catch((err) => {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
      }
    }).finally(() => setLoading(false));
    loadIndexNowStatus();
  }, [navigate]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}/status?status=${status}`, {}, getAuthHeaders());
      setLeads(leads.map(l => l.id === id ? {...l, status} : l));
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <div className="bg-slate-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/trio-logo.png" alt="Trio Insurance" className="h-10 w-auto" />
            <div>
              <p className="text-xs text-slate-400">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-slate-400 hover:text-white transition-colors">
              View Site
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-slate-400 hover:text-red-400 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab("leads")}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === "leads" ? "bg-red-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            data-testid="admin-leads-tab"
          >
            Leads
          </button>
          <button
            onClick={() => setActiveTab("blog")}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === "blog" ? "bg-red-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            data-testid="admin-blog-tab"
          >
            Blog
          </button>
          <button
            onClick={() => setActiveTab("indexnow")}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === "indexnow" ? "bg-red-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            data-testid="admin-indexnow-tab"
          >
            IndexNow
          </button>
        </div>

        {activeTab === "blog" ? (
          <AdminBlogManager token={localStorage.getItem("adminToken")} api={API} />
        ) : activeTab === "indexnow" ? (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-2">IndexNow — Instant Search Engine Submission</h3>
              <p className="text-slate-500 text-sm mb-4">Submit all your pages to Bing, Yandex, and other search engines instantly.</p>
              {indexNowStatus && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-black text-red-600">{indexNowStatus.total_urls}</div>
                    <div className="text-xs text-slate-500 mt-1">Total Pages</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-black text-green-600">{indexNowStatus.last_submission ? 'Active' : 'Not Yet'}</div>
                    <div className="text-xs text-slate-500 mt-1">Submission Status</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-sm font-bold text-slate-700">{indexNowStatus.last_submission ? new Date(indexNowStatus.last_submission.submitted_at).toLocaleString() : 'Never'}</div>
                    <div className="text-xs text-slate-500 mt-1">Last Submitted</div>
                  </div>
                </div>
              )}
              <button onClick={submitAllToIndexNow} disabled={indexNowLoading} data-testid="indexnow-submit-all"
                className={`w-full py-3 rounded-lg font-bold text-white transition-all ${indexNowLoading ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}>
                {indexNowLoading ? 'Submitting...' : 'Submit All Pages to Search Engines'}
              </button>
              {indexNowResult && !indexNowResult.error && (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-700 font-medium">Submitted {indexNowResult.total_urls} URLs to Bing, Yandex & search engines</p>
                  {indexNowResult.results?.map((r, i) => <p key={i} className="text-green-600 text-sm mt-1">Batch {i + 1}: HTTP {r.status_code} — {r.submitted} URLs</p>)}
                </div>
              )}
              {indexNowResult?.error && <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-700 font-medium">{indexNowResult.error}</p></div>}
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h4 className="font-bold text-slate-900 mb-2">How It Works</h4>
              <ul className="text-sm text-slate-500 space-y-2">
                <li className="flex items-start gap-2"><span className="text-red-600 font-bold">1.</span> Click "Submit All Pages" to send all URLs to search engines</li>
                <li className="flex items-start gap-2"><span className="text-red-600 font-bold">2.</span> Bing, Yandex, and other engines receive instant notification</li>
                <li className="flex items-start gap-2"><span className="text-red-600 font-bold">3.</span> New blog posts are auto-submitted every Monday</li>
                <li className="flex items-start gap-2"><span className="text-red-600 font-bold">4.</span> Google is NOT part of IndexNow — use Google Search Console for Google</li>
              </ul>
            </div>
          </div>
        ) : (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: "Total Leads", value: stats?.total || 0, color: "bg-blue-500" },
            { label: "New", value: stats?.new || 0, color: "bg-green-500" },
            { label: "Contacted", value: stats?.contacted || 0, color: "bg-yellow-500" },
            { label: "Converted", value: stats?.converted || 0, color: "bg-red-500" }
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center mb-4`}>
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
              <div className="text-slate-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900">Recent Leads</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leads-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">City</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Practice</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                      No leads yet. They'll appear here when visitors submit the quote form.
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">{lead.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-600">{lead.email}</div>
                        <div className="text-sm text-slate-600">{lead.phone}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{lead.city}</td>
                      <td className="px-6 py-4 text-slate-600">
                        {lead.practice_name || "-"}
                        {lead.employee_count && <span className="text-xs text-slate-400 block">{lead.employee_count} employees</span>}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          lead.status === "new" ? "bg-green-100 text-green-700" :
                          lead.status === "contacted" ? "bg-yellow-100 text-yellow-700" :
                          lead.status === "converted" ? "bg-blue-100 text-blue-700" :
                          "bg-slate-100 text-slate-700"
                        }`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={lead.status}
                          onChange={(e) => updateStatus(lead.id, e.target.value)}
                          className="text-sm border border-slate-300 rounded px-2 py-1"
                          data-testid={`status-select-${lead.id}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="lost">Lost</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <button data-testid={`delete-lead-${lead.id}`} onClick={async () => { if (window.confirm(`Delete lead ${lead.name}?`)) { try { await axios.delete(`${API}/admin/leads/${lead.id}`, getAuthHeaders()); setLeads(leads.filter(l => l.id !== lead.id)); } catch { alert('Failed to delete'); } } }} className="text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Cities */}
        {stats?.by_city?.length > 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Top Cities by Leads</h3>
            <div className="space-y-3">
              {stats.by_city.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-slate-700">{item._id}</span>
                  <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
                    {item.count} leads
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/workers-comp-dental/:slug" element={<CityPage />} />
          <Route path="/dental-malpractice-insurance/:slug" element={<IndustryCityPage />} />
          <Route path="/dental-disability-insurance/:slug" element={<IndustryCityPage />} />
          <Route path="/dental-general-liability/:slug" element={<IndustryCityPage />} />
          <Route path="/dental-employment-practices/:slug" element={<IndustryCityPage />} />
          <Route path="/dental-property-insurance/:slug" element={<IndustryCityPage />} />
          <Route path="/dental-cyber-liability/:slug" element={<IndustryCityPage />} />
          <Route path="/hartford-dental-workers-comp/:slug" element={<IndustryCityPage />} />
          <Route path="/cities" element={<CitiesPage />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
        </Routes>
        <CookieConsent />
      </BrowserRouter>
    </div>
  );
}

export default App;
